/**
 * US-standard Employer Identification Number (EIN) / Tax ID utilities.
 *
 * An EIN (also known as a Federal Tax Identification Number) is a nine-digit number
 * assigned by the IRS to business entities in the United States.
 *
 * Standard format: XX-XXXXXXX (e.g., 12-3456789)
 */

/**
 * Formats a raw or partially typed string into standard EIN format (XX-XXXXXXX).
 * Disallows alphabets and special characters, allowing only digits and the standard hyphen.
 */
export const formatTaxId = (val?: string | null): string => {
  if (!val) return "";
  const digits = val.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
};

/**
 * Checks whether a given string is a valid 9-digit US Tax ID (EIN).
 * Accepts formatted 'XX-XXXXXXX' or unhyphenated 9-digit 'XXXXXXXXX'.
 * Rejects non-digits, all-zeros, and incorrect hyphen positioning.
 */
export const isValidTaxId = (taxId?: string | null): boolean => {
  if (!taxId || typeof taxId !== "string") return false;
  const trimmed = taxId.trim();

  // Must match either XX-XXXXXXX or 9 digits XXXXXXXXX
  if (!/^\d{2}-\d{7}$/.test(trimmed) && !/^\d{9}$/.test(trimmed)) {
    return false;
  }

  const digits = trimmed.replace(/\D/g, "");
  if (digits.length !== 9) return false;

  // EIN cannot be all zeros
  if (/^0+$/.test(digits)) return false;

  return true;
};

/**
 * Validates a Tax ID input value according to US standards.
 *
 * @param val The input value to validate
 * @param isRequired Whether the field is mandatory
 * @param requiredMessage Custom message when mandatory and empty
 * @returns Error message string or empty string if valid
 */
export const validateTaxId = (
  val?: string | null,
  isRequired = false,
  requiredMessage = "Billing Provider Tax ID is required."
): string => {
  if (!val || !val.trim()) {
    return isRequired ? requiredMessage : "";
  }
  const trimmed = val.trim();
  if (!isValidTaxId(trimmed)) {
    return "Please enter a valid 9-digit Tax ID (EIN).";
  }
  return "";
};
